/**
 * Quays: the stone walls a city's canals and harbours lie between, the
 * ground level with the street right up to them.
 *
 * The terrain is a grid about 6.8 m a cell, and the water is cut into it a
 * cell at a time (`carveWater` in `cityterrain.ts`): every cell its water
 * touches goes down to its bed, the corners on land too. Under a built-up
 * shore that was a slope of up to a cell, from the street down to the water:
 * Stora Hamnkanalen's banks ran down into it, and the bus shelters by it
 * stood halfway down. A heightfield cannot hold a wall.
 *
 * So along each edge of a water polygon where the shore is built up:
 * - **The ground** within a cell of the edge, on land and in the water, is
 *   put back to the street's level, as it was before the water was cut in
 *   (`levelQuays`). The square's paving runs flat to the edge, and the slope
 *   down to the bed starts out in the water.
 * - **The water** is painted into the tile's landcover as `quayWater`
 *   (`paintQuayWater`), and the terrain shader draws no ground there: what
 *   the ground kept at the street's level over the water hides, and the
 *   wall and the water show.
 * - **A wall** stands at the edge, straight down from the street into the
 *   water, with a coping along its top. Walking off the quay stops at it.
 *
 * Pure: no fetch, no DOM.
 */

import type { TerrainMeshData } from './terrainmesh.js';
import { LANDCOVER } from '../sources/shortbread.js';
import { fillTriangle } from './landcover.js';

/** Pieces the wall is laid in along the edge, metres. */
const ALONG = 4;
/** How many stretches each piece is sampled in for a road or a bridge crossing it: a footbridge is two metres wide. */
const CROSS_SAMPLES = 4;
/** The wall's foot under the water, metres. */
const FOOT = 1.5;
/** The coping along the wall's top: how far out over the water, how far back, and its lip over the street. */
const COPING_OUT = 0.25;
/** Wide enough over the street to cover where the landcover raster's edge, a metre and a third a pixel, cuts into it. */
const COPING_BACK = 0.8;
const COPING = 0.03;
/** How built-up a water's shore must be, on average, for it to have quays, of 1. */
const BUILT = 0.35;
/** The wall's lowest over the water, metres: a quay, not a beach. */
const LEAST = 0.6;
/**
 * How far in from the edge the street's height is read, metres: the highest
 * of these. The elevation model, a metre a sample, smears the wall into a
 * slope a few metres wide, and read just inside the edge the street was
 * still on it: the ground between the quay's footways stood a metre under them.
 */
const STREET = [1.2, 3, 5, 7];

/**
 * How far a canal's water lies under its quays at the least, metres. The
 * elevation model, a metre a sample, smears the wall into the water, and its
 * level read off the shore was half a metre under the street, where Stora
 * Hamnkanalen lies two and a half under it.
 */
const QUAY_HEIGHT = 2.4;

/** Linear RGB: the wall's stone, and the granite coping. */
const STONE = [0.27, 0.26, 0.25];
const GRANITE = [0.42, 0.41, 0.4];

/** One stretch of quay: its edge, its land side, and the street's height at each end. */
export interface QuayEdge {
  ax: number; az: number; bx: number; bz: number;
  /** Unit, toward the land. */
  nx: number; nz: number;
  ha: number; hb: number;
  /** The water polygon it walls, by its index in `ranges`. */
  polygon: number;
  /** The street's own height at its middle, however near the water lies under it. */
  ground: number;
}

export interface Quays {
  edges: QuayEdge[];
  /** Which water polygons have a quay, by their index in `ranges`. */
  polygons: Set<number>;
  /** The walls and copings, lit. */
  mesh: { positions: Float32Array; colors: Float32Array; indices: Uint32Array };
  /** The walls, x0, z0, x1, z1, bottom, top each, to collide with. */
  walls: Float32Array;
}

export interface QuayInput {
  /** Water polygons: x, y, z per vertex (y their level), triangles, and per polygon its first vertex and count. */
  positions: Float32Array;
  indices: Uint32Array;
  ranges: Uint32Array;
  /** The ground before the water was cut into it. */
  land: (x: number, z: number) => number;
  /** How built-up (x, z) is, 0 to 1. */
  built: (x: number, z: number) => number;
  /** Whether a road or a bridge's deck crosses (x, z), over the water: the wall stops no one walking onto it there. */
  crossed?: (x: number, z: number) => boolean;
  /** Whether (x, z) is in water: where two polygons meet, the edge between them is no shore. */
  water: (x: number, z: number) => boolean;
  width: number;
  depth: number;
}

export function buildQuays(input: QuayInput): Quays {
  const { positions: P, indices: I, ranges, land } = input;
  const out = { positions: [] as number[], colors: [] as number[], indices: [] as number[] };
  const walls: number[] = [];
  const edges: QuayEdge[] = [];
  const polygons = new Set<number>();
  const quad = (a: number[], b: number[], c: number[], d: number[], col: number[]) => {
    const base = out.positions.length / 3;
    out.positions.push(...a, ...b, ...c, ...d);
    for (let k = 0; k < 4; k++) out.colors.push(col[0], col[1], col[2]);
    out.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  const polygonOf = (v: number) => {
    for (let r = 0; r + 1 < ranges.length; r += 2) if (v >= ranges[r] && v < ranges[r] + ranges[r + 1]) return r / 2;
    return -1;
  };
  // The polygons' outlines: edges only one of their triangles has, each with
  // the triangle's third corner, which is on the water's side of it.
  const count = new Map<string, { a: number; b: number; inside: number; n: number }>();
  for (let t = 0; t + 2 < I.length; t += 3) {
    for (let e = 0; e < 3; e++) {
      const a = I[t + e], b = I[t + (e + 1) % 3], c = I[t + (e + 2) % 3];
      const key = a < b ? `${a},${b}` : `${b},${a}`;
      const had = count.get(key);
      if (had === undefined) count.set(key, { a, b, inside: c, n: 1 });
      else had.n++;
    }
  }
  const onTileEdge = (x: number, z: number) => x < 0.5 || z < 0.5 || x > input.width - 0.5 || z > input.depth - 0.5;
  // The shore: each outline edge with land beside it, and how built-up.
  // Whether a water has quays is asked of all its shore, not of each edge:
  // a canal walled along its streets has its walls past a square or a park
  // too, and an edge left a slope there, under its water hidden, was a pit.
  interface Shore { a: number; b: number; ax: number; az: number; bx: number; bz: number; nx: number; nz: number; len: number; level: number; polygon: number }
  const shore: Shore[] = [];
  const builtUp = new Map<number, { built: number; len: number }>();
  for (const { a, b, inside, n } of count.values()) {
    if (n !== 1) continue;
    const ax = P[a * 3], az = P[a * 3 + 2], bx = P[b * 3], bz = P[b * 3 + 2];
    const level = Math.min(P[a * 3 + 1], P[b * 3 + 1]);
    // A tile's cut through the water is no shore.
    if (onTileEdge(ax, az) && onTileEdge(bx, bz)) continue;
    const ex = bx - ax, ez = bz - az, len = Math.hypot(ex, ez);
    if (len < 0.3) continue;
    // The land's side: away from the triangle's third corner.
    let nx = -ez / len, nz = ex / len;
    const cx = P[inside * 3] - ax, cz = P[inside * 3 + 2] - az;
    if (cx * nx + cz * nz > 0) { nx = -nx; nz = -nz; }
    const mx = (ax + bx) / 2, mz = (az + bz) / 2;
    if (input.water(mx + nx * 1.5, mz + nz * 1.5)) continue;
    const polygon = polygonOf(a);
    shore.push({ a, b, ax, az, bx, bz, nx, nz, len, level, polygon });
    const had = builtUp.get(polygon) ?? { built: 0, len: 0 };
    had.built += input.built(mx + nx * 3, mz + nz * 3) * len;
    had.len += len;
    builtUp.set(polygon, had);
  }
  for (const { ax, az, bx, bz, nx, nz, len, level, polygon } of shore) {
    const up = builtUp.get(polygon);
    if (up === undefined || up.built < BUILT * up.len) continue;
    const ex = bx - ax, ez = bz - az;
    polygons.add(polygon);
    const ground = (x: number, z: number) => STREET.reduce((h, d) => Math.max(h, land(x + nx * d, z + nz * d)), -Infinity);
    const street = (x: number, z: number) => Math.max(level + LEAST, ground(x, z));
    // In pieces along it, each at the street's height where it is.
    const pieces = Math.max(1, Math.ceil(len / ALONG));
    for (let p = 0; p < pieces; p++) {
      const t0 = p / pieces, t1 = (p + 1) / pieces;
      const x0 = ax + ex * t0, z0 = az + ez * t0, x1 = ax + ex * t1, z1 = az + ez * t1;
      const h0 = street(x0, z0), h1 = street(x1, z1);
      edges.push({ ax: x0, az: z0, bx: x1, bz: z1, nx, nz, ha: h0, hb: h1, polygon, ground: ground((x0 + x1) / 2, (z0 + z1) / 2) });
      // The wall down into the water, both faces, and the coping along its top.
      quad([x0, level - FOOT, z0], [x1, level - FOOT, z1], [x1, h1, z1], [x0, h0, z0], STONE);
      quad([x1, level - FOOT, z1], [x0, level - FOOT, z0], [x0, h0, z0], [x1, h1, z1], STONE);
      const o0 = [x0 - nx * COPING_OUT, h0 + COPING, z0 - nz * COPING_OUT], o1 = [x1 - nx * COPING_OUT, h1 + COPING, z1 - nz * COPING_OUT];
      const i0 = [x0 + nx * COPING_BACK, h0 + COPING, z0 + nz * COPING_BACK], i1 = [x1 + nx * COPING_BACK, h1 + COPING, z1 + nz * COPING_BACK];
      quad(o0, o1, i1, i0, GRANITE);
      quad([o0[0], h0 - 0.2, o0[2]], [o1[0], h1 - 0.2, o1[2]], o1, o0, GRANITE);
      // Walking off the quay stops at it, but not where a road or a bridge
      // runs on from it over the water: sampled along it, a metre out.
      let crossed = false;
      for (let k = 0; k <= CROSS_SAMPLES && input.crossed !== undefined && !crossed; k++) {
        const t = k / CROSS_SAMPLES;
        crossed = input.crossed(x0 + (x1 - x0) * t - nx, z0 + (z1 - z0) * t - nz);
      }
      if (!crossed) walls.push(x0, z0, x1, z1, level - FOOT, Math.max(h0, h1) + 0.9);
    }
  }
  return {
    edges, polygons,
    mesh: { positions: new Float32Array(out.positions), colors: new Float32Array(out.colors), indices: new Uint32Array(out.indices) },
    walls: new Float32Array(walls),
  };
}

/**
 * Each water with quays lowered to `QUAY_HEIGHT` under its quays' street, the
 * middle of their heights, wherever it lay higher. `positions` are x, y, z,
 * y its level. True if any of it moved.
 */
export function lowerQuayWater(positions: Float32Array, ranges: Uint32Array, edges: readonly QuayEdge[]): boolean {
  const streets = new Map<number, number[]>();
  for (const e of edges) {
    const list = streets.get(e.polygon) ?? [];
    list.push(e.ground);
    streets.set(e.polygon, list);
  }
  let changed = false;
  for (const [polygon, list] of streets) {
    if (polygon < 0) continue;
    list.sort((a, b) => a - b);
    const level = list[list.length >> 1] - QUAY_HEIGHT;
    const start = ranges[polygon * 2], count = ranges[polygon * 2 + 1];
    for (let v = start; v < start + count; v++) {
      if (positions[v * 3 + 1] > level) { positions[v * 3 + 1] = level; changed = true; }
    }
  }
  return changed;
}

/**
 * The ground within a cell of each quay put back to the street's level: on
 * the land, as it was before the water was cut (`before`), and no lower than
 * the street, where the elevation model sloped down to the water; in the water, the
 * street's height at the edge, so the slope down to the bed starts out in
 * it, where the ground is not drawn. True if any of it moved.
 */
export function levelQuays(mesh: TerrainMeshData, before: Float32Array, edges: readonly QuayEdge[]): boolean {
  if (edges.length === 0) return false;
  const { gridRes: res, gridHeights: heights, width, depth } = mesh;
  const side = res + 1, cell = Math.max(width, depth) / res, reach = cell * 1.5;
  let changed = false;
  for (const e of edges) {
    const ex = e.bx - e.ax, ez = e.bz - e.az, len2 = ex * ex + ez * ez;
    const minX = Math.min(e.ax, e.bx) - reach, maxX = Math.max(e.ax, e.bx) + reach;
    const minZ = Math.min(e.az, e.bz) - reach, maxZ = Math.max(e.az, e.bz) + reach;
    for (let row = Math.max(1, Math.floor((minZ / depth) * res)); row <= Math.min(res - 1, Math.ceil((maxZ / depth) * res)); row++) {
      for (let col = Math.max(1, Math.floor((minX / width) * res)); col <= Math.min(res - 1, Math.ceil((maxX / width) * res)); col++) {
        const v = row * side + col;
        const x = mesh.positions[v * 3], z = mesh.positions[v * 3 + 2];
        const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - e.ax) * ex + (z - e.az) * ez) / len2)) : 0;
        const px = e.ax + ex * t, pz = e.az + ez * t;
        if (Math.hypot(x - px, z - pz) > reach) continue;
        const street = e.ha + (e.hb - e.ha) * t - 0.02;
        // On the land, back up to where it was, and to the street at least;
        // in the water, up to the street.
        const want = (x - px) * e.nx + (z - pz) * e.nz >= 0 ? Math.max(before[v], street) : street;
        if (heights[v] < want - 1e-3) {
          heights[v] = want;
          changed = true;
        }
      }
    }
  }
  return changed;
}

/**
 * The water polygons with quays painted into the tile's landcover raster as
 * `quayWater`, for the terrain shader to draw no ground there. The raster's
 * row 0 is the tile's north edge, as chunk-local z runs south.
 */
export function paintQuayWater(raster: Uint8Array, size: number, width: number, depth: number,
  positions: Float32Array, indices: Uint32Array, ranges: Uint32Array, polygons: ReadonlySet<number>): void {
  if (polygons.size === 0 || raster.length < size * size) return;
  const sx = size / width, sz = size / depth;
  const inside = (v: number) => {
    for (const p of polygons) if (v >= ranges[p * 2] && v < ranges[p * 2] + ranges[p * 2 + 1]) return true;
    return false;
  };
  for (let t = 0; t + 2 < indices.length; t += 3) {
    const a = indices[t], b = indices[t + 1], c = indices[t + 2];
    if (!inside(a)) continue;
    fillTriangle(raster, size, LANDCOVER.quayWater,
      positions[a * 3] * sx, positions[a * 3 + 2] * sz, positions[b * 3] * sx, positions[b * 3 + 2] * sz,
      positions[c * 3] * sx, positions[c * 3 + 2] * sz);
  }
}
